import { useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { useProjects } from '../../state/projects';
import { isUnfinished, isWebApp } from '@/webapp/webAppRecord';
import type { AppDefinition } from '@/types/app';
import ChatPanel from './ChatPanel';
import PreviewFrame from './PreviewFrame';
import PublishPanel from './PublishPanel';
import UnsupportedRuntime from './UnsupportedRuntime';
import UnfinishedBuild from './UnfinishedBuild';
import SiteLine from './SiteLine';
import { Link } from 'react-router';
import { useSiteCheck } from '../../state/siteStatus';
import { accountsIn, addSignInRequest, reachOf, signInSummary } from '../../state/people';

/**
 * Chat and preview.
 *
 * Below 1024px they are two views with a toggle, as on the phone. From 1024px both are
 * on screen at once and the toggle disappears rather than being disabled — the extra
 * width buys a capability instead of rearranging the same one.
 */
export default function ProjectEditor({ mode }: { mode?: 'chat' | 'preview' }) {
  const { id = '' } = useParams();
  const { user } = useSession();
  const [params] = useSearchParams();
  // A new entry on every navigation — including the build's own, which lands on the SAME
  // /project/:id when it fills a reserved row, and must not keep showing the unfinished one.
  const { key: visit } = useLocation();
  const project = useProjects((s) => s.currentProject);
  const [view, setView] = useState<'chat' | 'preview'>(mode ?? (params.get('mode') === 'preview' ? 'preview' : 'chat'));
  // A chat edit on its way. It publishes itself when it lands, so Publish waits for it.
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    void useProjects.getState().loadProject(id, user?.id ?? '');
  }, [id, user?.id, visit]);

  /*
   * Whether the page is up, asked of the server once and shown in two places: beside the address
   * and at the top of the Your website panel, which is also what changes it. Asked here, above
   * the early returns, because it is a hook; only an app that can have a page has one to ask about.
   */
  const opened = project && project.id === id ? (project.definition as AppDefinition) : null;
  const pageable = opened && isWebApp(opened) && !isUnfinished(opened) ? opened : null;
  const [site, setSite] = useSiteCheck(pageable?.hostedAppId);

  if (!project || project.id !== id) return <p className="type-body">Loading…</p>;

  const definition = project.definition as AppDefinition;
  if (!isWebApp(definition)) return <UnsupportedRuntime runtime={definition.runtime ?? 'engine'} />;
  // Read by the MARK, never by the source: a gate failure can leave text that is not an app.
  if (isUnfinished(definition)) return <UnfinishedBuild project={project} />;

  const source = definition.source ?? '';
  /*
   * Whether the site has sign-in, in the phone's sentence (state/people.ts). The owner asked how
   * to "enable user management" and found nothing: People lived two screens away under My Apps,
   * and nothing said whether the site had accounts at all.
   */
  const accounts = accountsIn(source);
  const reach = reachOf(site);
  const signIn = signInSummary(
    accounts,
    reach,
    definition.hostedAppId ? 'Everyone who has signed in is listed under People using this site.' : '',
  );

  return (
    <div className="editor">
      <header className="editor-head">
        <div className="editor-title">
          <h1 className="type-title">{project.name}</h1>
          <SiteLine webUrl={definition.webUrl} site={site} />
        </div>
        <div className="editor-toggle" role="tablist" aria-label="Editor view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'chat'}
            className={view === 'chat' ? 'is-selected' : ''}
            onClick={() => setView('chat')}
          >
            Chat
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'preview'}
            className={view === 'preview' ? 'is-selected' : ''}
            onClick={() => setView('preview')}
          >
            Preview
          </button>
        </div>
      </header>

      <div className="editor-split">
        <div className={view === 'chat' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
          <ChatPanel
            projectId={id}
            appId={definition.id}
            appName={project.name}
            goal={project.description}
            source={source}
            onBusyChange={setEditing}
          />
        </div>
        <div className={view === 'preview' ? 'editor-pane' : 'editor-pane is-hidden-narrow'}>
          <PreviewFrame name={project.name} source={source} />
        </div>
      </div>

      {/*
        * Outside the panes, so it is reachable in BOTH views. A build lands here in
        * preview mode, and while publishing lived inside the chat pane it was
        * display:none at phone width — the app was finished and there was no way to put
        * it on the internet without knowing to switch tabs first.
        */}
      <PublishPanel project={project} editing={editing} site={site} onSiteChange={setSite} />
      <section className="editor-people" aria-label="Sign-in">
        <p className="type-body">
          <strong>{signIn.headline}</strong> {signIn.detail}
        </p>
        {accounts === 'none' && (
          <p className="type-caption">{`To add it, ask in Chat: “${addSignInRequest(definition.plan, source)}”`}</p>
        )}
      </section>
      <p className="type-body editor-links">
        <Link to={`/submissions/${id}`}>See replies people sent</Link>
        {/*
          * Only once the site has a catalogue id: People is read from the server under it, and
          * an app that was never published has nobody to list.
          */}
        {definition.hostedAppId && (
          <>
            {' · '}
            <Link to={`/users/${definition.hostedAppId}`} state={{ accounts, reach }}>People using this site</Link>
          </>
        )}
      </p>
    </div>
  );
}
